import type { TaskDetailModalStore } from "@components/widgets/Modals/ModalCommunication/store/task-detail-store";
import { t } from "@lingui/macro";
import { captureEvent } from "@sentry/react";
import {
  DeleteTaskDocument,
  type DeleteTaskMutation,
  type DeleteTaskMutationVariables,
  EditTaskWatchersDocument,
  type EditTaskWatchersMutation,
  type EditTaskWatchersMutationVariables,
  StarsStarableTypeEnum,
  TaskPermissionTypeEnum,
  type TaskWatcher,
} from "@src/__generated__/graphql";
import { TASK_ID_QUERY_KEY } from "@src/constants/tasks";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import { trackEvent } from "@src/services/amplitude";
import type { AppStore } from "@src/stores/AppStore";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import { action, computed, makeObservable } from "mobx";

/** The header actions: star, watch, share, edit, move and delete. */
export class TaskActionsStore {
  watchersPopover = new DisclosureState();
  deleteModal = new DisclosureState();
  editModal = new DisclosureState<{ moveMode: boolean }>();

  private editWatchersMutator = new MutationHelper<
    EditTaskWatchersMutation,
    EditTaskWatchersMutationVariables
  >(EditTaskWatchersDocument);
  private deleteTaskMutator = new MutationHelper<
    DeleteTaskMutation,
    DeleteTaskMutationVariables
  >(DeleteTaskDocument);

  constructor(
    private readonly appStore: AppStore,
    private readonly detailStore: TaskDetailModalStore,
  ) {
    makeObservable(this);
  }

  @computed get isWatching(): boolean {
    const userId = this.appStore.authStore.user?.id;
    if (!userId) return false;

    return !!this.detailStore.task.value?.taskWatchersIds.includes(userId);
  }

  @computed get isUpdatingWatchers(): boolean {
    return this.editWatchersMutator.isLoading.value;
  }

  @computed get isStarLoading(): boolean {
    const taskId = this.detailStore.task.value?.id;
    if (!taskId) return false;

    return this.appStore.starStore.isLoading(
      taskId,
      StarsStarableTypeEnum.Task,
    );
  }

  @computed get taskLink(): string {
    return `${location.origin}/notifications?${TASK_ID_QUERY_KEY}=${this.detailStore.taskId.value}`;
  }

  @action.bound
  reset(): void {
    this.watchersPopover.onClose();
  }

  @action.bound
  openEdit(): void {
    const { permissions } = this.detailStore;
    void permissions.runIfAllowed(permissions.updateType, this.editModal.open);
  }

  @action.bound
  openMove(): void {
    const { permissions } = this.detailStore;
    void permissions.runIfAllowed(permissions.updateType, () =>
      this.editModal.onOpen({ moveMode: true }),
    );
  }

  @action.bound
  openDelete(): void {
    void this.detailStore.permissions.runIfAllowed(
      TaskPermissionTypeEnum.Delete,
      this.deleteModal.open,
    );
  }

  @action.bound
  async toggleStar(): Promise<void> {
    const task = this.detailStore.task.value;
    if (!task) return;

    const { starStore } = this.appStore;
    const isStarred = !task.is_starred;
    const [, error] = isStarred
      ? await starStore.star(task.id, StarsStarableTypeEnum.Task)
      : await starStore.unstar(task.id, StarsStarableTypeEnum.Task);
    if (error) return;

    task.is_starred = isStarred;
    this.appStore.UIStore.toast({
      title: isStarred ? t`Task starred` : t`Task unstarred`,
      status: "success",
    });
  }

  @action.bound
  async updateWatchers(userIds: TaskWatcher["user_id"][]): Promise<void> {
    const taskId = this.detailStore.taskId.value;
    if (!Array.isArray(userIds) || !taskId) {
      this.appStore.UIStore.toast({ status: "error" });
      captureEvent({ message: "FE: Invalid input for updating task watchers" });
      return;
    }

    const [data, error] = await this.editWatchersMutator.mutate({
      taskId,
      userIds,
    });
    if (error) return;

    this.detailStore.task.value?.setWatchers(
      data.editTaskWatchers.taskWatchers,
    );
  }

  @action.bound
  async copyTaskLink(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.taskLink);
      this.appStore.UIStore.toast({
        title: t`Link copied to clipboard`,
        status: "info",
        isClosable: true,
        duration: 3000,
      });
    } catch (error) {
      console.error("Failed to copy task link", error);
    }
  }

  @action.bound
  async deleteTask(): Promise<void> {
    const task = this.detailStore.task.value;
    if (!task) return;

    const [, error] = await this.deleteTaskMutator.mutate({ id: task.id });
    if (error) return;

    this.appStore.UIStore.toast({
      title: t`The task has been deleted`,
      status: "success",
    });
    this.detailStore.disclosureState.additionalData?.onDelete?.(task);
    this.appStore.tasksListingStore.removeTask(task.id, task.parent?.id);
    this.detailStore.disclosureState.onClose();
    trackEvent("task", "Deleted task");
  }
}
